import React, {memo, useEffect, useState} from "react";
import { useTranslation } from 'react-i18next';
import {Col, Row} from "antd";
import moment from "moment";

// Components
import EmptyListMessage from "sharedComponents/EmptyListMessage/EmptyListMessage";
import CategoryBadge from "sharedComponents/CategoryBadge";
import TextWithLinks from "sharedComponents/TextWithLinks";
import LoadingSpinner from "sharedComponents/LoadingSpinner";
import UserSurveyQuestions from "./components/UserSurveyQuestions/UserSurveyQuestions.js";

// Hooks
import useDeviceType from "context/deviceType";
import {useNotification} from "../../../../../../../../../../Hooks";

// Utils
import {externalSurvey} from "models/externalSurvey";
import {upcomingSurvey} from "../../../../../../../../../../pages/Surveys/surveys.config";

// Assets
import eleapLogo from "../../../../../../../../../../assets/images/eleap-logo-dark.svg";

// Styles
import "./style/user_specific_survey.scss";
import note from "../../../../../../../../../../Hooks/useNotification";

const SurveyForm = ({ surveyData }) => {
    const {isMobile} = useDeviceType();

    const { t } = useTranslation();
    const [surveyForm, SetSurveyForm] = useState(null);
    const [isLoading, SetLoading] = useState(true);
    let mandatoryRefs = [];
    const note = useNotification;

    const isUpcomingSurvey = surveyData.survey_status === upcomingSurvey;

    useEffect(() => {
        if (isUpcomingSurvey) {
            SetLoading(false);
            return;
        }

        externalSurvey.getSurveyForm(surveyData.id).then(res => {
            if (res?.data?.success) {
                SetSurveyForm(res.data.form);

                if (res.data.error_message) {
                    note(
                        'error',
                        res.data.error_message,
                    );
                }
            }
        }).finally(() => SetLoading(false));

    }, [surveyData]);

    return (
        <Col span={24}>
            <Row type="flex" justify="center" className="user_survey">
                <Col span={24} xl={15} className="user_survey_col">
                    <div className="user_specific_survey">
                        <div className="specific_survey-header">
                            <div className="specific_survey-header-content">
                                <h2 className="survey_title">
                                    {surveyData.title}
                                </h2>
                            </div>
                        </div>

                        <div className="date_sect">
                            <div>
                                <span className="timer">
                                    <span className="note">!</span>
                                </span>
                                <span className="date">{moment(surveyData.survey_deadline).format("LLL")}</span>

                                {surveyData.survey_status !== "overdue" && < span className="line_v"/>}
                                {surveyData.survey_status === "overdue" ? '' :  moment(surveyData.survey_deadline).diff(moment(), 'days') ? <>
                                    <span className="day">{moment(surveyData.survey_deadline).diff(moment(), "days")} {t("days")}</span>
                                    <span className="remaining">{t("remaining")}</span>
                                </> : <span className="day">{t("Less than 24 hours")}</span>}

                                {!isMobile && surveyData.category && (
                                    <CategoryBadge
                                        category={surveyData.category}
                                        className="survey_category_badge_form"
                                    />
                                )}
                            </div>
                            <div className="external-survey_logo">
                                <span className="text">{t("Powered by")}</span>
                                <a className="external-survey_logo-link" href="https://performance.eleapsoftware.com" target="_blank">
                                    <img loading="lazy" src={eleapLogo} alt="logo" width="60" height="32.73" />
                                </a>
                            </div>
                        </div>

                        <div className="survey_desc">
                            <TextWithLinks as="span">{surveyData.description}</TextWithLinks>
                        </div>

                        {isUpcomingSurvey && <EmptyListMessage message={t("The survey has not started yet.")} />}

                        {isLoading ? (
                            <LoadingSpinner size="large" />
                        ) : !!surveyForm && (
                            <>
                                <UserSurveyQuestions
                                    formData={surveyForm}
                                    surveyId={surveyData.id}
                                    survey={surveyData}
                                    mandatoryRefs={mandatoryRefs}
                                />
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </Col>
    );
};

export default memo(SurveyForm);
